<div class="wizard-container">
  <p-toast position="top-right" role="status" aria-live="polite" aria-atomic="true"></p-toast>

  <!-- Skip to main content link for accessibility -->
  <a 
    href="#main-content" 
    class="skip-link"
    tabindex="0"
  >
    Vai al contenuto principale
  </a>

  <!-- Loading Spinner Overlay -->
  @if (isLoading()) {
    <div class="loading-overlay" role="status" aria-live="polite" aria-atomic="true">
      <div class="loading-content">
        <p-progressSpinner ariaLabel="Caricamento in corso"></p-progressSpinner>
        <p class="mt-3">Caricamento in corso...</p>
      </div>
    </div>
  }

  <!-- Content -->
  <div id="main-content" class="step-content" tabindex="-1">
    <!-- Page Header -->
    <header class="page-header mb-5" role="banner">
      <div class="header-content">
        <div class="header-left">
          <i class="pi pi-file-edit header-icon" aria-hidden="true"></i>
          <div class="header-text">
            <h1 id="contract-signature-heading">Firma del Contratto</h1>
            <p class="subtitle" id="page-subtitle">Scarica il contratto, firmalo e carica il documento firmato</p>
          </div>
        </div>
        <!-- Completion Indicator -->
        <div class="completion-badge" [attr.aria-label]="isCompleted() ? 'Step completato' : 'Step non completato'">
          @if (isCompleted()) {
            <i class="pi pi-check-circle completed-icon" aria-hidden="true"></i>
            <span class="completion-text">Completato</span>
          }
          @else {
            <i class="pi pi-circle incomplete-icon" aria-hidden="true"></i>
            <span class="completion-text">Da completare</span>
          }
        </div>
      </div>
    </header>

    @if (isCompleted()) {
      <!-- Completed state: Show only download of signed contract -->
      <section 
        class="surface-0 border-round p-5 mb-4 shadow-1" 
        aria-labelledby="completed-section-title"
        role="region"
      >
        <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
          <i class="pi pi-check-circle text-2xl" style="color: #10b981" aria-hidden="true"></i>
          <h2 id="completed-section-title" class="text-xl font-semibold m-0" style="color: #00125b">
            Contratto Firmato Inviato
          </h2>
        </div>

        <div class="bg-green-50 border-round p-4 mb-4">
          <div class="flex align-items-center gap-3">
            <i class="pi pi-check-circle text-4xl" style="color: #10b981" aria-hidden="true"></i>
            <div class="flex-1">
              <h3 class="text-lg font-semibold m-0 mb-1" style="color: #00125b">
                Processo Completato
              </h3>
              <p class="text-color-secondary m-0 text-sm">
                Il tuo contratto firmato è stato inviato con successo. Ora è in attesa della controfirma da parte di AXA.
              </p>
            </div>
          </div>
        </div>

        <p id="completed-download-description" class="text-color-secondary mb-4 line-height-3">
          Puoi scaricare il contratto firmato che hai inviato e, quando disponibile, il documento controfirmato da AXA.
        </p>

        <div class="flex align-items-center gap-3">
          <p-button
            label="Scarica Contratto Firmato"
            icon="pi pi-download"
            (onClick)="downloadSignedContract()"
            severity="success"
            [outlined]="true"
            [attr.aria-label]="'Scarica il contratto firmato che hai inviato'"
            [attr.aria-describedby]="'completed-download-description'"
            pTooltip="Scarica il contratto firmato da te"
            tooltipPosition="top"
          />
          @if (finalContractUrl()) {
            <p-button
              label="Scarica Contratto Controfirmato AXA"
              icon="pi pi-file-pdf"
              (onClick)="downloadFinalContract()"
              severity="success"
              [attr.aria-label]="'Scarica il contratto definitivo controfirmato da AXA'"
              pTooltip="Scarica il contratto definitivo controfirmato da AXA"
              tooltipPosition="top"
            />
          }
          @if (!isCompleted()) {
            <p-button
              label="Sostituisci Documento"
              icon="pi pi-refresh"
              (onClick)="replaceDocument()"
              severity="secondary"
              [outlined]="true"
              pTooltip="Carica un nuovo documento firmato in sostituzione di quello già inviato"
              tooltipPosition="top"
              [attr.aria-label]="'Sostituisci il documento firmato con uno nuovo'"
            />
          }
        </div>
      </section>
    } @else {
      <!-- Upload workflow: Download unsigned, upload signed -->
      <!-- Sezione: Scarica Contratto -->
      <section 
        class="surface-0 border-round p-5 mb-4 shadow-1" 
        aria-labelledby="download-section-title"
        role="region"
      >
        <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
          <i class="pi pi-download text-2xl" style="color: #00125b" aria-hidden="true"></i>
          <h2 id="download-section-title" class="text-xl font-semibold m-0" style="color: #00125b">
            1. Scarica il Contratto
          </h2>
        </div>

        <p id="download-section-description" class="text-color-secondary mb-4 line-height-3">
          Scarica il contratto da firmare. Una volta firmato, potrai caricarlo nella sezione successiva.
        </p>

        <div class="flex justify-content-start">
          <p-button
            label="Scarica Contratto"
            icon="pi pi-download"
            [loading]="isDownloading()"
            [disabled]="!canDownload()"
            (onClick)="downloadContract()"
            [attr.aria-label]="'Scarica il contratto da firmare in formato PDF'"
            [attr.aria-describedby]="'download-section-description'"
            [pTooltip]="downloadTooltip()"
            tooltipPosition="top"
          />
        </div>
      </section>

      <!-- Sezione: Carica Contratto Firmato -->
    <section 
      class="surface-0 border-round p-5 mb-4 shadow-1"
      aria-labelledby="upload-section-title"
      role="region"
    >
      <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
        <i class="pi pi-upload text-2xl" style="color: #00125b" aria-hidden="true"></i>
        <h2 id="upload-section-title" class="text-xl font-semibold m-0" style="color: #00125b">
          2. Carica il Contratto Firmato
        </h2>
        @if (isConfirmed()) {
          <p-tag 
            value="Confermato" 
            severity="success" 
            icon="pi pi-check"
            [attr.aria-label]="'Stato: Confermato'"
          ></p-tag>
        } @else if (hasUploadedContract()) {
          <p-tag 
            value="In Attesa di Conferma" 
            severity="warn" 
            icon="pi pi-clock"
            [attr.aria-label]="'Stato: In attesa di conferma'"
          ></p-tag>
        }
      </div>

      <!-- Status announcements for screen readers -->
      <div 
        aria-live="polite" 
        aria-atomic="true" 
        class="sr-only"
        role="status"
      >
        @if (isConfirmed()) {
          Contratto confermato e inviato al backoffice
        } @else if (hasUploadedContract()) {
          File caricato, in attesa di conferma
        } @else if (isConfirming()) {
          Invio del contratto in corso
        }
      </div>

      @if (isConfirmed()) {
        <!-- Read-only mode: Contract confirmed -->
        <div 
          class="bg-green-50 border-round p-4 mb-4" 
          role="status"
          aria-labelledby="confirmed-status-title"
          aria-describedby="confirmed-status-description"
        >
          <div class="flex align-items-center gap-3">
            <i class="pi pi-check-circle text-4xl" style="color: #10b981" aria-hidden="true"></i>
            <div class="flex-1">
              <h3 id="confirmed-status-title" class="text-lg font-semibold m-0 mb-1" style="color: #00125b">
                Contratto Firmato Confermato
              </h3>
              <p id="confirmed-status-description" class="text-color-secondary m-0 text-sm">
                Il tuo contratto firmato è stato inviato al backoffice per la verifica
              </p>
            </div>
          </div>
        </div>

        <p id="confirmed-download-instructions" class="text-color-secondary mb-4 line-height-3">
          Puoi scaricare il contratto firmato che hai inviato utilizzando il pulsante qui sotto.
        </p>

        <div class="flex justify-content-start">
          <p-button
            label="Scarica Contratto Firmato"
            icon="pi pi-download"
            (onClick)="downloadSignedContract()"
            severity="success"
            [outlined]="true"
            [attr.aria-label]="'Scarica il contratto firmato che hai inviato'"
            [attr.aria-describedby]="'confirmed-download-instructions'"
            pTooltip="Scarica il tuo contratto firmato"
            tooltipPosition="top"
          />
        </div>
      } @else {
        <!-- Upload mode: Not confirmed yet -->
        <p id="upload-section-description" class="text-color-secondary mb-4 line-height-3">
          Dopo aver firmato il contratto, caricalo qui sotto. Accettiamo solo file in formato PDF.
          <strong>Una volta confermato, non potrai più modificare il documento.</strong>
        </p>

        <!-- File Upload with Drag & Drop -->
        <div class="mb-4">
          <label id="file-upload-label" class="sr-only">
            Carica contratto firmato in formato PDF
          </label>
          <p-fileUpload
            #fileUpload
            name="signedContract"
            [auto]="false"
            [customUpload]="true"
            (onSelect)="onFileUpload($event)"
            [accept]="acceptedFileTypes"
            [maxFileSize]="maxFileSize"
            [disabled]="!canUpload() || isConfirming()"
            [showUploadButton]="false"
            [showCancelButton]="false"
            styleClass="custom-fileupload"
            [attr.aria-labelledby]="'file-upload-label'"
            [attr.aria-describedby]="'upload-section-description file-upload-help'"
            class="w-full"
          >
            <ng-template pTemplate="header">
              <!-- Header vuoto per nascondere i pulsanti di default -->
            </ng-template>
            <ng-template
              pTemplate="content"
              let-files
              let-chooseCallback="chooseCallback"
              let-removeFileCallback="removeFileCallback"
            >
              @if (isConfirming()) {
                <div 
                  class="flex align-items-center justify-content-center gap-2 p-5"
                  role="status"
                  aria-live="assertive"
                >
                  <i class="pi pi-spin pi-spinner text-2xl" aria-hidden="true"></i>
                  <span class="font-semibold">Caricamento e invio in corso...</span>
                </div>
              } @else if (files && files.length === 0) {
                <div class="flex flex-column align-items-center justify-content-center p-5 text-center">
                  <i class="pi pi-cloud-upload text-6xl mb-3 text-400" aria-hidden="true"></i>
                  <p class="m-0 mb-2 font-semibold text-900">Trascina qui il contratto firmato</p>
                  <p class="m-0 mb-3 text-sm text-600">oppure</p>
                  <p-button
                    label="Scegli File"
                    icon="pi pi-folder-open"
                    (onClick)="chooseCallback()"
                    [outlined]="true"
                    [disabled]="!canUpload()"
                    [attr.aria-label]="'Apri finestra per selezionare il contratto firmato da caricare'"
                  ></p-button>
                </div>
              } @else {
                <div class="p-3" role="list" aria-label="File selezionati">
                  @for (file of files; track file.name) {
                    <div
                      class="flex align-items-center justify-content-between p-3 border-1 border-200 border-round mb-2"
                      role="listitem"
                    >
                      <div class="flex align-items-center gap-3">
                        <i class="pi pi-file-pdf text-3xl text-red-500" aria-hidden="true"></i>
                        <div>
                          <div class="font-semibold text-900" id="file-name-{{file.name}}">{{ file.name }}</div>
                          <small class="text-600" aria-label="Dimensione file">{{ formatFileSize(file.size) }}</small>
                        </div>
                      </div>
                      <p-button
                        icon="pi pi-times"
                        [rounded]="true"
                        [text]="true"
                        severity="danger"
                        (onClick)="removeUploadedFile(removeFileCallback, file)"
                        [attr.aria-label]="'Rimuovi file ' + file.name"
                        [attr.aria-describedby]="'file-name-' + file.name"
                      ></p-button>
                    </div>
                  }
                  <p-button
                    label="Scegli un Altro File"
                    icon="pi pi-folder-open"
                    (onClick)="chooseCallback()"
                    [outlined]="true"
                    size="small"
                    class="w-full mt-2"
                    [attr.aria-label]="'Sostituisci il file selezionato con un altro'"
                  ></p-button>
                </div>
              }
            </ng-template>
          </p-fileUpload>
        </div>

        <div 
          id="file-upload-help" 
          class="flex align-items-center gap-2 text-color-secondary text-sm mb-4"
          role="note"
        >
          <i class="pi pi-info-circle" aria-hidden="true"></i>
          <span>File accettati: PDF • Dimensione massima: 10 MB</span>
        </div>

        <!-- Confirm Button - Always visible -->
        <div 
          class="bg-blue-50 border-round p-4 mb-4"
          role="complementary"
          aria-labelledby="confirm-section-title"
          aria-describedby="confirm-section-description"
        >
          <div class="flex align-items-start gap-3">
            <i 
              class="pi text-2xl" 
              [ngClass]="hasUploadedContract() ? 'pi-exclamation-triangle' : 'pi-info-circle'"
              [style.color]="hasUploadedContract() ? '#f59e0b' : '#3b82f6'" 
              aria-hidden="true"
            ></i>
            <div class="flex-1">
              <h3 id="confirm-section-title" class="text-base font-semibold m-0 mb-2" style="color: #00125b">
                {{ hasUploadedContract() ? 'Conferma il Caricamento' : 'Carica un File per Continuare' }}
              </h3>
              <p id="confirm-section-description" class="text-sm text-color-secondary m-0 mb-3 line-height-3">
                @if (hasUploadedContract()) {
                  Una volta confermato, il documento verrà inviato al backoffice per la verifica.
                  <strong>Non potrai più modificare o sostituire il documento.</strong>
                } @else {
                  Seleziona un file PDF firmato per abilitare il pulsante di conferma e procedere con l'invio al backoffice.
                }
              </p>
              <p-button
                label="Conferma e Invia al Backoffice"
                icon="pi pi-send"
                [loading]="isConfirming()"
                [disabled]="!canConfirm()"
                (onClick)="confirmAndUpload()"
                severity="success"
                [attr.aria-label]="'Conferma e invia il contratto firmato al backoffice per la verifica'"
                [attr.aria-describedby]="'confirm-section-description confirm-button-tooltip'"
                [pTooltip]="confirmButtonTooltip()"
                tooltipPosition="top"
              />
              <!-- Tooltip content for screen readers -->
              <span id="confirm-button-tooltip" class="sr-only">
                {{ confirmButtonTooltip() }}
              </span>
            </div>
          </div>
        </div>
      }
    </section>
    }
  </div>
</div>
